import { ReasonToBuyUI } from "./ReasonToBuyUI";
import { StandardProps } from "../componentMap";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { TickCircleOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/TickCircleOutlined";
import { DeliveryOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/DeliveryOutlined";
import { GiftCardOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/GiftCardOutlined";
import { GuaranteesAndTroubleshootingOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/GuaranteesAndTroubleshootingOutlined";
import { pricingGroups } from "@/utils/product/getPromoMessaging";
import {
  EXCLUDED_CATEGORIES,
  EXCLUDED_PRODUCT_ID,
  EXCLUDED_PRODUCT_TYPE,
  EXCLUDED_BRANDS,
} from "@/utils/pricePromise/PricePromiseExclusions.ts";
import { ProtectPlusSvg } from "../PostAddToBasketDrawer/components/ProtectPlusCompliance/ProtectPlusSvg";
import { getScheme } from "../PostAddToBasketDrawer/scheme/schemeDecisioner";
import { replaceVariables } from "../PostAddToBasketDrawer/utils";
import { formatTimeToAmPm } from "@/utils/date/formatTime";
import { ReactNode, useMemo, useState } from "react";
import { PricePromiseDrawer } from "../PricePromise/PricePromiseDrawer";
import { WaitroseLogoSvg } from "../ClickAndCollectCountdown/utils/WaitroseLogoSvg";
import {
  DMS_IDS,
  FREE_DELIVERY_THRESHOLD_DMS_IDS,
} from "@/constants/constants";
import {
  isProtectPlusExtendedWarranty,
  isIncludedGuarantee,
} from "./utils/utils";
import {
  getProductTypeHierarchy,
  isHomeProductTypeHierarchy,
  isNurseryProductTypeHierarchy,
} from "./utils/productTypeHierarchy";
import { sendEvent } from "@JohnLewisPartnership/experiments-utils";

export type Card = {
  id: string;
  title: string;
  description?: string | undefined;
  descriptionHref?: string | undefined;
  onDescriptionClick?: () => void;
  icon?: ReactNode;
  linkUrl?: string | undefined;
  linkText?: string | undefined;
};

type CardProps = Card[];

type AvailableOption = {
  aliases?: { dmsId?: string | null } | null;
  earliestFulfilment?: {
    orderByLocalTimeToday?: string | null;
  } | null;
};

const BEAUTY_CATEGORY_ID = "50000296";
const BEAUTY_EXCLUDED_SUBCATEGORY_IDS = [
  "300000005268",
  "80000702",
  "80000688",
  "800001975",
  "800001974",
];

function buildClickAndCollectCard(
  option: AvailableOption | undefined,
): Card | null {
  if (!option) return null;

  const orderByLocalTimeToday =
    option.earliestFulfilment?.orderByLocalTimeToday;

  if (orderByLocalTimeToday) {
    const [h, m] = orderByLocalTimeToday.split(":").map((t) => parseInt(t, 10));
    const cutoffTime = new Date();
    cutoffTime.setHours(h || 0, m || 0, 0, 0);
    const timeLeftMs = cutoffTime.getTime() - Date.now();

    if (timeLeftMs > 0) {
      // IN HOURS: before cutoff (includes early morning before pre-countdown window)
      return {
        id: "clickandcollect",
        title: "Get it tomorrow",
        description: `Order before ${formatTimeToAmPm(orderByLocalTimeToday)} and collect from your nearest Waitrose`,
        icon: <WaitroseLogoSvg />,
      };
    }
  }

  // OUT OF HOURS: after cutoff, or no earliestFulfilment data (e.g. Saturday)
  return {
    id: "clickandcollect",
    title: "Collect from your nearest Waitrose",
    icon: <WaitroseLogoSvg />,
  };
}

export function ReasonToBuy({ product }: Pick<StandardProps, "product">) {
  const { variant } = useVariant(product);
  const protectPlusScheme = getScheme(product);
  const [isPricePromiseDrawerOpen, setIsPricePromiseDrawerOpen] =
    useState(false);

  const isJohnLewisProduct =
    typeof product?.brand?.name === "string" &&
    ["john lewis", "anyday", "and/or", "kin"].some((brand) =>
      product?.brand?.name.toLowerCase().includes(brand),
    );

  const includedGuarantee = variant.services?.find(isIncludedGuarantee);
  const protectPlusItem =
    variant.services?.filter(isProtectPlusExtendedWarranty)?.[0] ?? null;

  const isPricePromiseEligible = useMemo(() => {
    const brandName = product?.brand?.name?.toLowerCase();

    const hasExclusiveAdditional = product?.messaging?.some(
      (msg) =>
        msg.type === "additional" &&
        typeof msg.title === "string" &&
        msg.title.toLowerCase().includes("exclusive"),
    );
    const productGroupHasPromos = product?.messaging?.some(
      (msg) =>
        "promotionGroup" in msg && pricingGroups.includes(msg.promotionGroup),
    );
    const categoryIsExcluded = product?.browsePageHierarchy?.some((page) =>
      EXCLUDED_CATEGORIES.includes(page.categoryId),
    );
    const brandIsExcluded =
      typeof brandName === "string" &&
      EXCLUDED_BRANDS.some((b) => brandName.includes(b));

    return !(
      hasExclusiveAdditional ||
      categoryIsExcluded ||
      brandIsExcluded ||
      product.aliases?.pimProductId === EXCLUDED_PRODUCT_ID ||
      product.__typename === EXCLUDED_PRODUCT_TYPE ||
      productGroupHasPromos
    );
  }, [product]);

  const clickAndCollectCard = useMemo(() => {
    const cncOption = product.fulfilmentOptions?.available?.find(
      (option) => option.aliases?.dmsId === DMS_IDS.ClickAndCollect,
    );
    return buildClickAndCollectCard(cncOption);
  }, [product]);

  const shouldShowBeautyPlaceholderCard = useMemo(() => {
    const hierarchyCategoryIds = (product?.browsePageHierarchy ?? []).map(
      (page) => `${page.categoryId}`,
    );

    const isBeautyCategory = hierarchyCategoryIds.includes(BEAUTY_CATEGORY_ID);
    const hasExcludedBeautySubcategory = hierarchyCategoryIds.some((id) =>
      BEAUTY_EXCLUDED_SUBCATEGORY_IDS.includes(id),
    );

    return isBeautyCategory && !hasExcludedBeautySubcategory;
  }, [product?.browsePageHierarchy]);

  const shouldShowNurseryCard = useMemo(() => {
    return isNurseryProductTypeHierarchy(getProductTypeHierarchy(product));
  }, [product]);

  const shouldShowHomeCard = useMemo(() => {
    return isHomeProductTypeHierarchy(getProductTypeHierarchy(product));
  }, [product]);

  const protectPlusCard = useMemo(() => {
    if (!protectPlusItem) return null;

    const templateVars = {
      VAR_YEARS: protectPlusItem.duration ?? "",
      VAR_PRICE: protectPlusItem.price?.display ?? "",
    };

    const highlight = replaceVariables(
      protectPlusScheme?.schemeHighlight || "",
      templateVars,
    );
    const marketingMessage = replaceVariables(
      protectPlusScheme?.schemeMarketingMessage || "",
      templateVars,
    );

    return {
      id: "protect-plus",
      title:
        highlight ||
        `Add ${templateVars.VAR_YEARS} years of cover for accidental damage: ${templateVars.VAR_PRICE}`,
      description:
        marketingMessage ||
        `Protect your item from accidental damage for ${templateVars.VAR_YEARS} years, in addition to your product's standard guarantee.`,
      icon: <ProtectPlusSvg />,
    };
  }, [protectPlusItem, protectPlusScheme]);

  // Delivery card: only shown when the product has a standard delivery option
  // qualifying for the free-delivery threshold (OnePersonDelivery, SupplierDelivery,
  // M2MDelivery, SupplierDeliveryCondor). Products with 2-man or paid-only delivery
  // (e.g. large appliances) will not have a matching DMS ID → card is omitted.
  const deliveryCard = useMemo(() => {
    const standardDeliveryOption = product.fulfilmentOptions?.available?.find(
      (option) =>
        option.aliases?.dmsId != null &&
        FREE_DELIVERY_THRESHOLD_DMS_IDS.includes(option.aliases.dmsId),
    );

    if (!standardDeliveryOption) return null;

    return {
      id: "delivery",
      title: standardDeliveryOption.headline,
      description: standardDeliveryOption.price?.display,
      icon: <DeliveryOutlined size="large" />,
    };
  }, [product]);

  // Split the cost card: only shown when the selected variant is credit-eligible
  // ("core") and has at least one credit offering ID. Variants with creditEligibility
  // of "addOn" or "excluded" (e.g. gift cards, food) will not show the card.
  const splitTheCostCard = useMemo(() => {
    if (variant.creditEligibility !== "core") return null;
    if (!variant.creditOfferingIds?.length) return null;

    return {
      id: "split-the-cost",
      title: "Split the cost",
      description: "Flexible payments available",
      icon: <GiftCardOutlined size="large" />,
    };
  }, [variant.creditEligibility, variant.creditOfferingIds]);

  const nextDayDeliveryOption = product.fulfilmentOptions?.available?.find(
    (option) => option.aliases?.dmsId === DMS_IDS.NextOrNamedDayDelivery,
  );
  const formattedOrderByTime = formatTimeToAmPm(
    nextDayDeliveryOption?.earliestFulfilment?.orderByLocalTimeToday,
  );

  const cards: CardProps = [
    clickAndCollectCard,
    includedGuarantee && {
      id: "guarantee",
      title: includedGuarantee.title,
      description: "Included with your purchase",
      icon: <GuaranteesAndTroubleshootingOutlined size="large" />,
    },
    shouldShowBeautyPlaceholderCard && {
      id: "my-jl-beauty",
      title: "MY JL BEAUTY",
      description: "Be rewarded as part of the MyJL Beauty community",
      linkUrl: "/our-services/my-john-lewis-beauty?source=pdprtb",
      linkText: "Join MyJL Beauty",
    },
    shouldShowHomeCard && {
      id: "home",
      title:
        "Free in-store or virtual consultations with our home design stylists",
      description: "",
      linkUrl:
        "https://www.johnlewis.com/our-services/home-design-service?source=pdprtb",
      linkText: "Book a home design appointment",
    },
    shouldShowNurseryCard && {
      id: "nursery",
      title: "Nursery reason to buy",
      description: "Placeholder copy",
    },
    deliveryCard,
    splitTheCostCard,
    isPricePromiseEligible && {
      id: "price-promise",
      title: "Our price promise",
      description: "Confidence in every purchase",
      onDescriptionClick: () => {
        setIsPricePromiseDrawerOpen(true);
        sendEvent("WPDP | Price Promise Info Displayed");
      },
      icon: <TickCircleOutlined size="large" />,
    },
    protectPlusCard,
  ].filter((card): card is Card => !!card);

  if (formattedOrderByTime && nextDayDeliveryOption) {
    cards.push({
      id: "next-day-delivery",
      title: "Next day delivery available",
      description: `Order by ${formattedOrderByTime} to receive by the next day. ${nextDayDeliveryOption.price.display}`,
      icon: <DeliveryOutlined size="large" />,
    });
  }

  if (!cards.length) return null;

  return (
    <>
      <ReasonToBuyUI cards={cards} />
      {isPricePromiseEligible && (
        <PricePromiseDrawer
          product={product}
          isOpen={isPricePromiseDrawerOpen}
          onClose={() => setIsPricePromiseDrawerOpen(false)}
          isJohnLewisProduct={isJohnLewisProduct}
        />
      )}
    </>
  );
}
